import React from "react";
import { Link } from "react-router-dom";

/**
 * Gradient pill-style chat header — avatar in circle overlaid on the left,
 * name + status text on a pink→purple gradient pill.
 *
 * Props:
 *   peer      : { user_id, name, picture }
 *   online    : boolean
 *   typing    : boolean
 *   lastSeen  : ISO string of peer.last_seen (used when offline)
 *   onClickName : optional callback when the name is clicked (replaces Link)
 *   children  : optional right-side controls (close, expand, etc.)
 */
function timeAgoShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function ChatHeaderPill({ peer, online, typing, lastSeen, onClickName, children }) {
  if (!peer) return null;
  const initial = (peer.name || "?")[0].toUpperCase();
  const statusNode = typing ? (
    <span data-testid="status-typing" className="text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-1 text-white/90">
      <span className="inline-flex gap-0.5">
        <span className="w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "0ms" }} />
        <span className="w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "150ms" }} />
        <span className="w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "300ms" }} />
      </span>
      typing
    </span>
  ) : online ? (
    <span
      data-testid="status-online"
      className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#1CE783]"
      style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
    >
      Online
    </span>
  ) : (
    <span className="flex items-baseline gap-1.5 leading-none">
      <span
        data-testid="status-offline"
        className="text-[10px] uppercase tracking-[0.22em] font-bold text-red-400"
        style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
      >
        Offline
      </span>
      {lastSeen && (
        <span data-testid="status-last-seen" className="text-[10px] text-white/65 normal-case tracking-normal">
          · last seen {timeAgoShort(lastSeen)}
        </span>
      )}
    </span>
  );

  const NameTag = onClickName ? "button" : Link;
  const nameProps = onClickName
    ? { type: "button", onClick: onClickName }
    : { to: `/u/${peer.user_id}` };

  return (
    <div className="relative pl-[60px] pr-3 py-2">
      {/* Avatar circle, overlaps the pill on the left */}
      <div
        className="absolute left-1 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border-2 border-black shadow-[0_4px_18px_rgba(255,42,109,0.4)] overflow-hidden bg-gradient-to-br from-[#FF2A6D] to-[#9D4CDD] flex items-center justify-center text-base font-extrabold text-white z-10"
        data-testid="chat-header-avatar"
      >
        {peer.picture ? (
          <img src={peer.picture} alt="" className="w-full h-full object-cover" />
        ) : (
          <span>{initial}</span>
        )}
      </div>

      {/* Gradient pill */}
      <div
        data-testid="chat-header-pill"
        className="rounded-full pl-12 pr-4 py-2 flex items-center gap-3"
        style={{
          background: "linear-gradient(90deg, #FF2A6D 0%, #C32B85 50%, #6D3CC9 100%)",
          boxShadow: "0 2px 16px rgba(157,76,221,0.35), inset 0 0 0 1px rgba(255,255,255,0.06)",
        }}
      >
        <div className="flex-1 min-w-0">
          <NameTag
            {...nameProps}
            data-testid="chat-header-name"
            className="block font-display font-extrabold text-white text-base tracking-tight truncate hover:underline text-left"
          >
            {peer.name}
          </NameTag>
          <div className="mt-0.5 leading-none">{statusNode}</div>
        </div>
        {children && <div className="flex items-center gap-1">{children}</div>}
      </div>
    </div>
  );
}
